import React from 'react'
import { Text, Box, HStack, FormControl, FormLabel, Stack, Radio, RadioGroup } from '@chakra-ui/react'
import { LabeledSelect, DatePickerInput, LabeledInput, Alert } from '@apex/component-library'
import { FormikErrors, FormikTouched, useFormikContext } from 'formik'
import {
  TransferRecurrenceText,
  TransferRecurrenceType,
  WhenToSendType,
  WhenToSendText,
  TransferFrequencyType,
  TransferFrequencyText
} from './Types'
import ScheduleTransfersInstructions from '../ScheduleTransfersInstructions'
import { COLUMN_WIDTH, MAX_COLUMN_WIDTH } from '../../utils'

export interface TransferFrequencyFormikValues {
  whenToSend: string
  scheduledDate: Date | string
  transferRecurrence: string
  transferFrequency: string
  startDate: Date | string
  transferScheduledDate: Date | string
  transferStartDate: Date | string
  numberOfTransfers?: number
}

interface TransferFrequencyProps {
  selectedRecurrence?: TransferRecurrenceType
  selectedWhenToSend?: WhenToSendType
  selectedScheduledDate?: Date | string
  selectedFrequency?: TransferFrequencyType
  selectedStartDate?: Date | string
  numberOfTransfers?: number
  isTotalBalanceTransfer: boolean
  isPreviousYearContribution?: boolean
}

const TransferFrequency = ({
  selectedRecurrence,
  selectedWhenToSend,
  selectedScheduledDate,
  selectedFrequency,
  selectedStartDate,
  numberOfTransfers,
  isTotalBalanceTransfer,
  isPreviousYearContribution
}: TransferFrequencyProps) => {
  const formik = useFormikContext()
  const errors: FormikErrors<TransferFrequencyFormikValues> = formik.errors
  const touched: FormikTouched<TransferFrequencyFormikValues> = formik.touched
  const { setFieldValue } = formik
  const clearForm = () => {
    setFieldValue('whenToSend', 'IMMEDIATELY')
    setFieldValue('transferScheduledDate', '')
    setFieldValue('transferFrequency', 'WEEKLY')
    setFieldValue('transferStartDate', '')
    setFieldValue('numberOfTransfers', '')
  }
  return (
    <>
      <Text as='h3' textStyle='h3'>
        Transfer Frequency
      </Text>
      <RadioGroup
        onChange={(value) => {
          clearForm()
          setFieldValue('transferRecurrence', value)
        }}
        value={selectedRecurrence}
      >
        <Stack direction='column'>
          <Radio value='ONE_TIME' display='inline'>
            {TransferRecurrenceText.ONE_TIME}
          </Radio>
          <Radio value='RECURRING' display='inline'>
            {TransferRecurrenceText.RECURRING}
          </Radio>
        </Stack>
      </RadioGroup>
      {selectedRecurrence === 'ONE_TIME' && (
        <HStack spacing={8}>
          <Box
            width={selectedWhenToSend === 'IN_THE_FUTURE' ? '50%' : COLUMN_WIDTH}
            maxW={MAX_COLUMN_WIDTH}
            height='5em'
          >
            <LabeledSelect
              label='When to send'
              isRequired
              name='whenToSend'
              aria-label='When to send'
              onBlur={formik.handleBlur}
              value={selectedWhenToSend}
              onChange={(e) => {
                formik.setFieldValue('whenToSend', e.target.value)
              }}
            >
              <option value='IMMEDIATELY'>{WhenToSendText.IMMEDIATELY}</option>
              <option value='IN_THE_FUTURE'>{WhenToSendText.IN_THE_FUTURE}</option>
            </LabeledSelect>
          </Box>
          {selectedWhenToSend === 'IN_THE_FUTURE' && (
            <Box width={'50%'} height='5em'>
              <FormControl>
                <FormLabel
                  fontWeight={'semibold'}
                  color={!!errors.transferScheduledDate && !!touched.transferScheduledDate ? 'red.500' : 'black'}
                >
                  Scheduled Date *
                </FormLabel>
                <DatePickerInput
                  data-testid='date-picker'
                  isInvalid={!!errors.transferScheduledDate && !!touched.transferScheduledDate}
                  onChange={(date: Date | string) => setFieldValue('transferScheduledDate', date)}
                  onBlur={(e) => {
                    formik.handleBlur(e)
                    formik.setFieldTouched('transferScheduledDate', true)
                  }}
                  date={selectedScheduledDate!}
                  disablePastDates={false}
                  disableFutureDates={false}
                  showActionsBar={false}
                  shortcuts={false}
                  minDate={new Date(Date.now() + 3600 * 1000 * 24)}
                  maxDate={new Date(Date.now() + 3600 * 1000 * 24 * 365 * 5)} // calendar only shows current month and year unless max date is set
                  placement={'bottom'}
                  highlightCurrentDay={true}
                />
              </FormControl>
            </Box>
          )}
        </HStack>
      )}
      {selectedRecurrence === 'RECURRING' && isTotalBalanceTransfer && (
        <Alert
          status='error'
          title='Error:'
          description='Recurring is not allowed for total balance transfers.'
          showIcon={false}
        />
      )}
      {selectedRecurrence === 'RECURRING' && isPreviousYearContribution && (
        <Alert
          status='error'
          title='Error:'
          description='Recurring is not allowed for previous year contributions.'
          showIcon={false}
        />
      )}
      {selectedRecurrence === 'RECURRING' && !isTotalBalanceTransfer && !isPreviousYearContribution && (
        <Stack spacing={4} paddingBottom={4}>
          <ScheduleTransfersInstructions />
          <Box width={COLUMN_WIDTH} maxW={MAX_COLUMN_WIDTH} height='5em'>
            <LabeledSelect
              label='Frequency'
              isRequired
              name='frequency'
              aria-label='Frequency'
              onBlur={formik.handleBlur}
              value={selectedFrequency}
              onChange={(e) => {
                formik.setFieldValue('transferFrequency', e.target.value)
              }}
            >
              <option value='WEEKLY'>{TransferFrequencyText.WEEKLY}</option>
              <option value='EVERY_OTHER_WEEK'>{TransferFrequencyText.EVERY_OTHER_WEEK}</option>
              <option value='MONTHLY'>{TransferFrequencyText.MONTHLY}</option>
              <option value='EVERY_3_MONTHS'>{TransferFrequencyText.EVERY_3_MONTHS}</option>
              <option value='EVERY_6_MONTHS'>{TransferFrequencyText.EVERY_6_MONTHS}</option>
              <option value='ANNUALLY'>{TransferFrequencyText.ANNUALLY}</option>
            </LabeledSelect>
          </Box>
          <Box width={COLUMN_WIDTH} maxW={MAX_COLUMN_WIDTH} height='5em'>
            <FormControl>
              <FormLabel
                fontWeight={'semibold'}
                color={!!errors.transferStartDate && touched.transferStartDate ? 'red.500' : 'black'}
              >
                Start Date *
              </FormLabel>
              <DatePickerInput
                data-testid='start-date-picker'
                isInvalid={!!errors.transferStartDate && touched.transferStartDate}
                onChange={(date: Date | string) => setFieldValue('transferStartDate', date)}
                onBlur={(e) => {
                  formik.handleBlur(e)
                  formik.setFieldTouched('transferStartDate', true)
                }}
                date={selectedStartDate!}
                disablePastDates
                disableFutureDates={false}
                showActionsBar={false}
                shortcuts={false}
                minDate={new Date(Date.now())}
                maxDate={new Date(Date.now() + 3600 * 1000 * 24 * 365 * 5)} // calendar only shows current month and year unless max date is set
                placement={'bottom'}
                highlightCurrentDay={true}
              />
            </FormControl>
          </Box>
          <Box width={COLUMN_WIDTH} maxW={MAX_COLUMN_WIDTH} height='5em'>
            <LabeledInput
              label='Number of Transfers'
              aria-label='Number of Transfers'
              helperText='If blank, schedule is active until canceled'
              name='numberOfTransfers'
              onBlur={formik.handleBlur}
              value={numberOfTransfers}
              onChange={(e) => {
                setFieldValue('numberOfTransfers', e.target.value.replace(/[^0-9-]*/g, ''))
              }}
              errorMessage={errors.numberOfTransfers}
              isInvalid={touched.numberOfTransfers && !!errors.numberOfTransfers}
            />
          </Box>
        </Stack>
      )}
    </>
  )
}

export default TransferFrequency
